import clsx from 'clsx';
import PropTypes from 'prop-types';
import React from 'react';
import { Navigation, Pagination, FreeMode, Mousewheel } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import Link from 'components/shared/link';

import dataSvg from './images/database.svg';
import engineeringSvg from './images/engineering.svg';
import leadershipSvg from './images/leadership.svg';
import marketingSvg from './images/marketing.svg';
import productSvg from './images/product.svg';
import supportSvg from './images/support.svg';

import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import 'swiper/css/free-mode';
import 'swiper/css/mousewheel';
import './slider.css';

const icons = {
  engineering: {
    icon: engineeringSvg,
    iconStyle: {
      filter:
        'drop-shadow(0px 6px 20px rgba(230, 233, 248, 0.70)) drop-shadow(0px 2px 8px rgba(92, 112, 214, 0.20))',
    },
    spanClassName: 'bg-[#C1C9F6]',
    borderGradientClassName: 'before:bg-[linear-gradient(to_right,#B8C2E080_0%,#EAEDF6_100%)]',
  },
  marketing: {
    icon: marketingSvg,
    iconStyle: {
      filter:
        'drop-shadow(0px 6px 20px rgba(248, 230, 230, 0.60)) drop-shadow(0px 2px 8px rgba(191, 64, 64, 0.12))',
    },
    spanClassName: 'bg-[#F7D4D4]',
    borderGradientClassName: 'before:bg-[linear-gradient(to_right,#EEDDE7_0%,#EDEDF3_100%)]',
  },
  product: {
    icon: productSvg,
    iconStyle: {
      filter:
        'drop-shadow(0px 6px 20px rgba(219, 240, 231, 0.60)) drop-shadow(0px 2px 8px rgba(76, 178, 136, 0.16))',
    },
    spanClassName: 'bg-[#C1F6E0]',
    borderGradientClassName: 'before:bg-[linear-gradient(to_right,#D0EEE2_0%,#EAF3EF_100%)]',
  },
  support: {
    icon: supportSvg,
    iconStyle: {
      filter:
        'drop-shadow(0px 6px 20px rgba(230, 233, 248, 0.70)) drop-shadow(0px 2px 8px rgba(92, 112, 214, 0.20))',
    },
    spanClassName: 'bg-[#C1C9F6]',
    borderGradientClassName: 'before:bg-[linear-gradient(to_right,#D6DBF5_0%,#EBEDF5_100%)]',
  },
  'data-teams': {
    icon: dataSvg,
    iconStyle: {
      filter:
        'drop-shadow(0px 6px 20px rgba(248, 230, 230, 0.60)) drop-shadow(0px 2px 8px rgba(191, 64, 64, 0.12))',
    },
    spanClassName: 'bg-[#F7D4D4]',
    borderGradientClassName: 'before:bg-[linear-gradient(to_right,#EEDDE7_0%,#EDEDF3_100%)]',
  },
  leadership: {
    icon: leadershipSvg,
    iconStyle: {
      filter:
        'drop-shadow(0px 6px 20px rgba(219, 240, 231, 0.60)) drop-shadow(0px 2px 8px rgba(76, 178, 136, 0.16))',
    },
    spanClassName: 'bg-[#C1F6E0]',
    borderGradientClassName: 'before:bg-[linear-gradient(to_right,#D0EEE2_0%,#EAF3EF_100%)]',
  },
};

const Products = ({ title, description, products }) => (
  <section className="products safe-paddings overflow-hidden relative pb-16 md:pb-0">
    <div className="container">
      <h2 className="text-new-black text-[56px] leading-denser font-medium tracking-tight-4 text-center xl:text-[48px] lg:text-[40px] md:text-[32px]">
        {title}
      </h2>
      {description && (
        <p className="mt-5 text-xl leading-snug font-light text-new-gray-1 text-center max-w-[592px] mx-auto xl:mt-4 xl:text-[18px] xl:max-w-[516px] lg:text-[16px] lg:max-w-[478px] md:mt-5">
          {description}
        </p>
      )}
      <Swiper
        className="mt-16 !static !overflow-visible xl:mt-12 lg:mt-10 md:mt-8"
        id="tools-swiper"
        pagination={{
          clickable: true,
        }}
        modules={[Navigation, Pagination, FreeMode, Mousewheel]}
        breakpoints={{
          768: {
            navigation: {
              enabled: true,
            },
            mousewheel: {
              enabled: true,
            },
            freeMode: {
              enabled: true,
            },
            cssMode: false,
            centeredSlides: false,
            centeredSlidesBounds: false,
          },
        }}
        navigation={{ enabled: false }}
        mousewheel={{ enabled: false }}
        freeMode={{ enabled: false }}
        slidesPerView="auto"
        centeredSlidesBounds
        centeredSlides
        cssMode
      >
        {products.map(({ title, description, linkText, linkUrl, iconName }, index) => {
          const { icon, iconStyle, spanClassName, borderGradientClassName } = icons[iconName];

          return (
            <SwiperSlide
              className="relative mr-7 !h-80 !w-auto last:mr-0 overflow-hidden rounded-2xl p-px xl:!h-[314px] xl:mr-6 lg:!h-[261px] lg:mr-[18px]"
              key={index}
            >
              <span
                className={clsx(
                  'w-[400px] h-[356px] absolute -top-32 -left-[124px] rounded-full block blur-[90px] opacity-[0.12] pointer-events-none',
                  spanClassName
                )}
              />
              <div
                className={clsx(
                  'w-96 h-full relative before:absolute before:-inset-px before:rounded-2xl xl:w-[304px] lg:w-[290px] md:w-[280px]',
                  borderGradientClassName
                )}
              >
                <div className="relative z-10 h-full flex flex-col p-6 pt-5 rounded-2xl bg-new-gray-2 lg:p-5">
                  <div
                    className="w-[60px] h-[60px] flex justify-center items-center bg-white rounded-full fill-[rgba(255,255,255,0.80)] xl:w-14 xl:h-14 lg:w-12 lg:h-12"
                    style={iconStyle}
                  >
                    <img
                      className="w-7 h-7"
                      loading="eager"
                      src={icon || null}
                      alt={title}
                      height={28}
                      width={28}
                    />
                  </div>
                  <h3 className="text-[28px] mt-[26px] leading-none text-new-black tracking-tight-4 xl:text-[24px] xl:mt-5 lg:mt-4 lg:text-[20px]">
                    {title}
                  </h3>
                  <p className="text-new-gray-1 mt-3.5 font-light text-base xl:max-w-64 lg:mt-2 lg:text-[14px] lg:leading-snug">
                    {description}
                  </p>
                  {linkText && linkUrl && (
                    <Link
                      className="mt-auto xl:text-[16px] lg:text-[14px]"
                      to={linkUrl}
                      size="md"
                      theme="secondary-2"
                      withArrow
                    >
                      <span className="sr-only">about {title}</span>
                      {linkText}
                    </Link>
                  )}
                </div>
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>
    </div>
  </section>
);

Products.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  products: PropTypes.arrayOf(
    PropTypes.shape({
      iconName: PropTypes.oneOf(Object.keys(icons)).isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      linkText: PropTypes.string,
      linkUrl: PropTypes.string,
    })
  ).isRequired,
};

Products.defaultProps = {
  description: null,
};

export default Products;
